import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, AreaChart, Area, Legend,
} from "recharts";
import {
  ChartPie, TrendUp, Coins, Buildings, Stack, Scales, ArrowsClockwise,
  CaretDown, CaretUp,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { Button, Input, Modal } from "./ui";
import { toast } from "sonner";

// Vibrant, article-grade palette (Carbon-derived accents + warm/gem tones)
const PALETTE = [
  "#0F62FE", "#24A148", "#F1C21B", "#DA1E28", "#8A3FFC", "#FF7EB6",
  "#08BDBA", "#FF832B", "#4589FF", "#D12771", "#005D5D", "#9F1853",
];
const LIQ_COLORS = { Liquid: "#24A148", "Semi-liquid": "#F1C21B", Illiquid: "#DA1E28" };

const fmt = (n, cur) =>
  `${cur ? cur + " " : ""}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmt2 = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function Kpi({ icon, label, value, sub, tone = "#0F62FE", testid, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
      data-testid={testid}
      className="relative overflow-hidden rounded-xl border border-black/10 bg-white/70 p-4 backdrop-blur-xl transition-all hover:-translate-y-px dark:border-white/10 dark:bg-white/5"
    >
      <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-15" style={{ background: tone }} />
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide opacity-70">
        <span style={{ color: tone }}>{icon}</span>{label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums" style={{ color: tone }}>{value}</div>
      {sub ? <div className="mt-0.5 text-xs opacity-60">{sub}</div> : null}
    </motion.div>
  );
}

function Panel({ title, icon, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-black/10 bg-white/60 p-4 backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <span className="text-[var(--primary)]">{icon}</span>{title}
      </div>
      {children}
    </div>
  );
}

const ttStyle = { background: "rgba(20,20,20,0.92)", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 };

export default function WealthDashboard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [base, setBase] = useState("USD");
  const [fxOpen, setFxOpen] = useState(false);
  const [fxRates, setFxRates] = useState({});
  const [newCode, setNewCode] = useState("");
  const [newRate, setNewRate] = useState("");

  const load = useCallback((b) => {
    setLoading(true);
    client.get(`/wealth/analytics${b ? `?base=${b}` : ""}`)
      .then((r) => { setData(r.data); setBase(r.data.base_currency); setFxRates(r.data.fx_rates || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};
  const cur = data?.base_currency || "USD";
  const comm = data?.commitments || {};

  const catData = useMemo(() => (data?.by_category || []).map((r) => ({ name: r.key, value: r.value, pct: r.pct })), [data]);
  const classData = useMemo(() => (data?.by_asset_class || []).slice(0, 10), [data]);
  const curData = useMemo(() => (data?.by_currency || []), [data]);
  const liqData = useMemo(() => (data?.by_liquidity || []), [data]);
  const top = data?.top_holdings || [];
  const trend = data?.trend || [];

  const saveFx = () => {
    client.put("/wealth/fx-rates", { base, rates: fxRates })
      .then(() => { toast.success("FX rates saved"); setFxOpen(false); load(base); })
      .catch(() => toast.error("Could not save FX rates"));
  };
  const addRate = () => {
    const code = (newCode || "").toUpperCase().trim();
    const rate = parseFloat(newRate);
    if (!code || !(rate > 0)) return;
    setFxRates((r) => ({ ...r, [code]: rate }));
    setNewCode(""); setNewRate("");
  };

  return (
    <div className="mb-4 rounded-2xl border border-[var(--primary)]/30 bg-gradient-to-br from-[var(--primary)]/5 to-transparent p-4" data-testid="wealth-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-base font-semibold">
          <ChartPie size={20} weight="duotone" className="text-[var(--primary)]" />
          Consolidated Wealth Analytics
          <span className="rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-[10px] font-medium text-[var(--primary)]">governed data · base {cur}</span>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="wealth-base-currency" value={base}
            onChange={(e) => { setBase(e.target.value); load(e.target.value); }}
            className="rounded-md border border-black/15 bg-transparent px-2 py-1 text-xs dark:border-white/15"
          >
            {["USD", "EUR", "GBP", "CHF", "CAD", "AUD", "JPY", "CNY", "AED", "SGD", ...Object.keys(fxRates)]
              .filter((v, i, a) => a.indexOf(v) === i)
              .map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button variant="ghost" className="text-xs" data-testid="wealth-fx-btn" onClick={() => setFxOpen(true)}>
            <ArrowsClockwise size={14} className="me-1" />FX rates
          </Button>
          <Button variant="ghost" className="text-xs" data-testid="wealth-refresh" onClick={() => load(base)}>Refresh</Button>
          <button data-testid="wealth-toggle" onClick={() => setOpen((o) => !o)} className="rounded-md p-1 hover:bg-black/5 dark:hover:bg-white/10">
            {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-4 space-y-4">
          {/* KPI ROW */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <Kpi testid="kpi-networth" icon={<Coins size={16} weight="duotone" />} tone="#0F62FE" delay={0.02}
              label="Net worth" value={fmt(t.net_worth, cur)} sub={`${t.holdings_count || 0} holdings`} />
            <Kpi testid="kpi-appraised" icon={<Buildings size={16} weight="duotone" />} tone="#08BDBA" delay={0.06}
              label="Appraised (FMV)" value={fmt(t.appraised, cur)} sub={`Gross ${fmt(t.gross, cur)}`} />
            <Kpi testid="kpi-pools" icon={<Stack size={16} weight="duotone" />} tone="#8A3FFC" delay={0.1}
              label="Currency pools" value={fmt(t.currency_pools, cur)} />
            <Kpi testid="kpi-committed" icon={<Scales size={16} weight="duotone" />} tone="#FF832B" delay={0.14}
              label="Called capital" value={fmt(comm.called, cur)} sub={`Uncalled ${fmt(comm.remaining, cur)}`} />
            <Kpi testid="kpi-moic" icon={<TrendUp size={16} weight="duotone" />} tone="#24A148" delay={0.18}
              label="Portfolio MOIC" value={comm.moic ? `${fmt2(comm.moic)}×` : "—"} sub={`DPI ${fmt2(comm.dpi)} · TVPI ${fmt2(comm.tvpi)}`} />
            <Kpi testid="kpi-xirr" icon={<TrendUp size={16} weight="duotone" />} tone={comm.xirr >= 0 ? "#24A148" : "#DA1E28"} delay={0.22}
              label="Portfolio XIRR" value={comm.xirr != null ? `${fmt2(comm.xirr)}%` : "—"} sub={`${comm.fund_count || 0} funds`} />
            <Kpi testid="kpi-twr" icon={<TrendUp size={16} weight="duotone" />} tone={comm.twr >= 0 ? "#0F62FE" : "#DA1E28"} delay={0.26}
              label="Portfolio TWR" value={comm.twr != null ? `${fmt2(comm.twr)}%` : "—"}
              sub={comm.twr != null ? "Time-weighted (Modified Dietz)" : "Needs capital-call log"} />
          </div>

          {data?.unconverted_currencies?.length ? (
            <div className="rounded-lg border border-[#F1C21B]/50 bg-[#F1C21B]/10 px-3 py-2 text-xs" data-testid="wealth-fx-warning">
              ⚠︎ No FX rate on file for {data.unconverted_currencies.join(", ")} — these amounts are counted at face value in {cur}. Add rates via <b>FX rates</b> for an accurate roll-up.
            </div>
          ) : null}

          {/* CHART GRID */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Panel title="Allocation by category" icon={<ChartPie size={16} weight="duotone" />} testid="panel-allocation">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={catData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {catData.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={ttStyle} formatter={(v, n, p) => [`${fmt(v, cur)} (${p.payload.pct}%)`, n]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel title="Exposure by asset class" icon={<Stack size={16} weight="duotone" />} testid="panel-assetclass">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={classData} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.12} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} />
                    <YAxis type="category" dataKey="key" tick={{ fontSize: 10 }} width={90} />
                    <Tooltip contentStyle={ttStyle} formatter={(v) => fmt(v, cur)} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {classData.map((e, i) => <Cell key={i} fill={PALETTE[(i + 3) % PALETTE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel title="Appraised net-worth trend (12 mo)" icon={<TrendUp size={16} weight="duotone" />} testid="panel-trend">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ left: 4, right: 8 }}>
                    <defs>
                      <linearGradient id="wealthGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0F62FE" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#0F62FE" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.12} />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => fmt(v)} width={60} />
                    <Tooltip contentStyle={ttStyle} formatter={(v) => fmt(v, cur)} />
                    <Area type="monotone" dataKey="value" stroke="#0F62FE" strokeWidth={2} fill="url(#wealthGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel title="Liquidity & currency mix" icon={<Scales size={16} weight="duotone" />} testid="panel-liquidity">
              <div className="grid grid-cols-2 gap-2">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={liqData} dataKey="value" nameKey="key" outerRadius={80}>
                        {liqData.map((e, i) => <Cell key={i} fill={LIQ_COLORS[e.key] || PALETTE[i]} />)}
                      </Pie>
                      <Tooltip contentStyle={ttStyle} formatter={(v, n, p) => [`${fmt(v, cur)} (${p.payload.pct}%)`, p.payload.key]} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={curData} dataKey="value" nameKey="key" innerRadius={35} outerRadius={80}>
                        {curData.map((e, i) => <Cell key={i} fill={PALETTE[(i + 6) % PALETTE.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={ttStyle} formatter={(v, n, p) => [`${fmt(v, cur)} (${p.payload.pct}%)`, p.payload.key]} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Panel>
          </div>

          {/* TOP HOLDINGS + CONCENTRATION */}
          <Panel title={`Top holdings · top-5 concentration ${data?.concentration_top5_pct || 0}%`} icon={<Buildings size={16} weight="duotone" />} testid="panel-concentration">
            {top.length ? (
              <div className="space-y-1.5">
                {top.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs" data-testid={`top-holding-${i}`}>
                    <div className="w-40 truncate font-medium">{h.name}</div>
                    <div className="relative h-4 flex-1 overflow-hidden rounded bg-black/5 dark:bg-white/10">
                      <div className="absolute inset-y-0 left-0 rounded" style={{ width: `${Math.min(h.pct, 100)}%`, background: PALETTE[i % PALETTE.length] }} />
                    </div>
                    <div className="w-24 text-end tabular-nums">{fmt(h.value, cur)}</div>
                    <div className="w-12 text-end tabular-nums opacity-60">{h.pct}%</div>
                  </div>
                ))}
              </div>
            ) : <div className="text-xs opacity-60">No holdings yet.</div>}
          </Panel>

          {/* FUND PERFORMANCE TABLE */}
          {data?.funds?.length ? (
            <Panel title="Private-fund performance" icon={<TrendUp size={16} weight="duotone" />} testid="panel-funds">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="funds-table">
                  <thead>
                    <tr className="border-b border-black/10 text-start opacity-60 dark:border-white/10">
                      <th className="py-1.5 pe-2">Fund</th>
                      <th className="px-2 text-end">Commitment</th>
                      <th className="px-2 text-end">Called</th>
                      <th className="px-2 text-end">Distributed</th>
                      <th className="px-2 text-end">NAV</th>
                      <th className="px-2 text-end">DPI</th>
                      <th className="px-2 text-end">TVPI</th>
                      <th className="px-2 text-end">MOIC</th>
                      <th className="px-2 text-end">XIRR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.funds.map((f, i) => (
                      <tr key={i} className="border-b border-black/5 tabular-nums dark:border-white/5" data-testid={`fund-row-${i}`}>
                        <td className="py-1.5 pe-2 font-medium">{f.name}</td>
                        <td className="px-2 text-end">{fmt(f.commitment, cur)}</td>
                        <td className="px-2 text-end">{fmt(f.called, cur)}</td>
                        <td className="px-2 text-end">{fmt(f.distributed, cur)}</td>
                        <td className="px-2 text-end">{fmt(f.nav, cur)}</td>
                        <td className="px-2 text-end">{fmt2(f.dpi)}</td>
                        <td className="px-2 text-end">{fmt2(f.tvpi)}</td>
                        <td className="px-2 text-end font-semibold">{fmt2(f.moic)}×</td>
                        <td className="px-2 text-end font-semibold" style={{ color: f.xirr == null ? "inherit" : f.xirr >= 0 ? "#24A148" : "#DA1E28" }}>
                          {f.xirr != null ? `${fmt2(f.xirr)}%` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          ) : null}

          {loading ? <div className="text-center text-xs opacity-50">Recomputing…</div> : null}
        </div>
      )}

      <Modal open={fxOpen} onClose={() => setFxOpen(false)} title={`FX rates → base ${base}`} testid="fx-modal">
        <div className="space-y-3">
          <p className="text-xs opacity-70">
            Enter how many units of <b>{base}</b> one unit of each currency is worth (rate to base).
            Holdings and currency pools are rolled up using these rates. No live market feed is used — you govern the rates.
          </p>
          <div className="max-h-56 space-y-1.5 overflow-y-auto">
            {Object.entries(fxRates).filter(([c]) => c !== base).map(([code, rate]) => (
              <div key={code} className="flex items-center gap-2" data-testid={`fx-row-${code}`}>
                <span className="w-14 font-mono text-xs">{code}</span>
                <Input type="number" value={rate} className="flex-1"
                  onChange={(e) => setFxRates((r) => ({ ...r, [code]: parseFloat(e.target.value) || 0 }))}
                  data-testid={`fx-input-${code}`} />
                <button className="text-xs text-[#DA1E28]" data-testid={`fx-del-${code}`}
                  onClick={() => setFxRates((r) => { const n = { ...r }; delete n[code]; return n; })}>Remove</button>
              </div>
            ))}
          </div>
          <div className="flex items-end gap-2 border-t border-black/10 pt-2 dark:border-white/10">
            <Input label="Currency" placeholder="EUR" value={newCode} onChange={(e) => setNewCode(e.target.value)} data-testid="fx-new-code" className="w-24" />
            <Input label="Rate to base" type="number" placeholder="1.08" value={newRate} onChange={(e) => setNewRate(e.target.value)} data-testid="fx-new-rate" className="flex-1" />
            <Button variant="ghost" onClick={addRate} data-testid="fx-add">Add</Button>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setFxOpen(false)}>Cancel</Button>
            <Button onClick={saveFx} data-testid="fx-save">Save rates</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
